import type { OptionDB } from "@project/emerge";
import {
  findSingleCreeperWheelEntryInLibrary,
  isSingleWheelCreeperEntry,
} from "@project/shared";
import { LTCaseUserOption } from "../enum/LTCaseUserOption";
import { ROLLER_TYPE } from "../enum/ROLLER_TYPE";
import { VariableOptionType } from "../enum/VariableOptionType";
import type { FlightCaseSettings } from "../interfaces/FlightCaseSettings";
import type {
  CreeperRollerType,
  RollerProperties,
  RollerType,
} from "../interfaces/RollerBoardProperties";

/** Non-enumerable markers on legacy exterior creeper (bundle) for A/B catalog rows */
const HYDRATED_WHEEL_A = "__hydratedWheelA";
const HYDRATED_WHEEL_B = "__hydratedWheelB";

export function isCreeperBundleMeshes(roller: CreeperRollerType): boolean {
  const m = roller.meshes as Record<string, unknown> | undefined;
  if (!m || typeof m !== "object") return false;
  return "rollerA" in m && "rollerB" in m;
}

export function isSingleWheelCreeperCatalogEntry(r: RollerType): boolean {
  return isSingleWheelCreeperEntry(r);
}

export function resolveSingleCasterFromNested(
  nested: RollerProperties | undefined,
  optionDb: OptionDB,
): CreeperRollerType | undefined {
  if (!nested?.name) return undefined;
  const all =
    optionDb.getAllOfKind<RollerType>(LTCaseUserOption.RollerType) ?? [];
  const match = findSingleCreeperWheelEntryInLibrary(nested.name, all);
  return match ? (match as CreeperRollerType) : undefined;
}

/** Preserve layout/board fields from imported creeper data when replacing with a catalog caster. */
export function buildCreeperResolutionSource(
  bundle: CreeperRollerType,
  nested: RollerProperties | undefined,
  slot: "A" | "B",
): CreeperRollerType {
  if (!nested) return bundle;
  const slotKey = slot === "A" ? "rollerA" : "rollerB";
  const existingSlot = bundle[slotKey];
  return {
    ...bundle,
    width: nested.width ?? bundle.width,
    [slotKey]: existingSlot ? { ...existingSlot, ...nested } : nested,
  };
}

export function mergeCreeperCasterWithSourceMetadata(
  source: CreeperRollerType,
  resolved: CreeperRollerType,
): CreeperRollerType {
  return {
    ...resolved,
    // Imported rollbrett board (1000b/1001b) must win over catalog caster defaults.
    board: source.board ?? resolved.board,
    width: source.width ?? resolved.width,
    rollerA: source.rollerA ?? resolved.rollerA,
    rollerB: source.rollerB ?? resolved.rollerB,
  };
}

function resolveCreeperSlotToCatalogSingle(
  roller: CreeperRollerType,
  wheel: "A" | "B",
  optionDb: OptionDB,
): CreeperRollerType {
  if (!isCreeperBundleMeshes(roller)) return roller;
  const nested = wheel === "A" ? roller.rollerA : roller.rollerB;
  const resolved = resolveSingleCasterFromNested(nested, optionDb);
  if (!resolved) return roller;
  return mergeCreeperCasterWithSourceMetadata(
    buildCreeperResolutionSource(roller, nested, wheel),
    resolved,
  );
}

function defineHidden(target: object, key: string, value: unknown): void {
  Object.defineProperty(target, key, {
    value,
    enumerable: false,
    configurable: true,
    writable: true,
  });
}

/**
 * After `migrateFlightCaseSettings`, replace imported rollbrett **bundle**
 * creeper data (meshes.rollerA/B, full board JSON) with catalog **single-caster**
 * rows (`meshes.default`, e.g. ROL3470) so downstream code matches freshly built
 * cases. Idempotent for already-clean options.
 *
 * Called from `FlightCaseDefinition` (constructor) so JSON/unit-test/debug
 * imports share one path without resolver logic in `RollerBuildStep`.
 */
export function hydrateImportedCreeperRollers(
  settings: FlightCaseSettings,
  optionDb: OptionDB,
): void {
  const rollers = settings.exteriorOptions?.rollers;
  if (
    rollers &&
    typeof rollers === "object" &&
    (rollers as CreeperRollerType).rollerType === ROLLER_TYPE.CREEPER &&
    isCreeperBundleMeshes(rollers as CreeperRollerType)
  ) {
    const cr = rollers as CreeperRollerType;
    const a = resolveSingleCasterFromNested(cr.rollerA, optionDb);
    const b = resolveSingleCasterFromNested(cr.rollerB, optionDb);
    if (a) {
      defineHidden(
        cr,
        HYDRATED_WHEEL_A,
        mergeCreeperCasterWithSourceMetadata(
          buildCreeperResolutionSource(cr, cr.rollerA, "A"),
          a,
        ),
      );
    }
    if (b) {
      defineHidden(
        cr,
        HYDRATED_WHEEL_B,
        mergeCreeperCasterWithSourceMetadata(
          buildCreeperResolutionSource(cr, cr.rollerB, "B"),
          b,
        ),
      );
    }
  }

  for (const opt of settings.variableOptions ?? []) {
    if (opt?.variableOptionType !== VariableOptionType.ROLLER_BOARD) {
      continue;
    }
    const rb = opt;
    if (rb.rollerA && isCreeperBundleMeshes(rb.rollerA)) {
      rb.rollerA = resolveCreeperSlotToCatalogSingle(rb.rollerA, "A", optionDb);
    }
    if (rb.rollerB && isCreeperBundleMeshes(rb.rollerB)) {
      rb.rollerB = resolveCreeperSlotToCatalogSingle(rb.rollerB, "B", optionDb);
    }
  }
}

/**
 * Legacy exterior stores one creeper bundle; synthetic {@link RollerBoardInfos}
 * needs distinct A/B catalog rows. Used only by {@link RollerBuildStep}
 * `createLegacyRollerBoardOption`.
 */
export function getHydratedLegacyCreeperWheel(
  creeper: CreeperRollerType,
  wheel: "A" | "B",
  optionDb: OptionDB,
): CreeperRollerType {
  const key = wheel === "A" ? HYDRATED_WHEEL_A : HYDRATED_WHEEL_B;
  const resolved = (creeper as Record<string, unknown>)[key] as
    | CreeperRollerType
    | undefined;
  if (resolved) return resolved;

  const nested = wheel === "A" ? creeper.rollerA : creeper.rollerB;
  const fromNested = resolveSingleCasterFromNested(nested, optionDb);
  if (fromNested) {
    return mergeCreeperCasterWithSourceMetadata(
      buildCreeperResolutionSource(creeper, nested, wheel),
      fromNested,
    );
  }

  if (isCreeperBundleMeshes(creeper)) {
    console.warn(
      `hydrateImportedCreeperRollers: could not resolve creeper wheel ${wheel} for ${creeper.name ?? "unknown"}`,
    );
  }
  return creeper;
}
